import React, { createContext, useContext, useState, useEffect } from 'react';
import { useMode } from './ModeContext';
import { useAuth } from './AuthContext';

const WorkspaceStateContext = createContext();

export const WorkspaceStateProvider = ({ children }) => {
  const { currentMode } = useMode();
  const { currentUser } = useAuth();
  
  // State for each workspace mode
  const [workspaceStates, setWorkspaceStates] = useState({
    individual: {
      lastRoute: '/dashboard',
      preferences: {},
      sidebarCollapsed: false,
      activeFilters: {},
      selectedItems: []
    },
    organization: {
      lastRoute: '/org/dashboard',
      preferences: {},
      sidebarCollapsed: false,
      activeFilters: {},
      selectedItems: [],
      currentDepartment: null,
      currentProject: null
    },
    admin: {
      lastRoute: '/admin/dashboard',
      preferences: {},
      sidebarCollapsed: false,
      activeFilters: {},
      selectedItems: [],
      currentView: 'overview'
    }
  });

  // Save state when switching modes
  const saveWorkspaceState = (mode, updates) => {
    setWorkspaceStates(prev => ({
      ...prev,
      [mode]: {
        ...prev[mode],
        ...updates
      }
    }));
  };

  // Get state for current mode
  const getCurrentWorkspaceState = () => {
    return workspaceStates[currentMode] || workspaceStates.individual;
  };

  // Update current mode state
  const updateCurrentState = (updates) => {
    saveWorkspaceState(currentMode, updates);
  };

  // Preserve user preferences across sessions
  useEffect(() => {
    if (currentUser?.id) {
      const savedStates = localStorage.getItem(`workspace_states_${currentUser.id}`);
      if (savedStates) {
        try {
          const parsed = JSON.parse(savedStates);
          setWorkspaceStates(prev => ({
            ...prev,
            ...parsed
          }));
        } catch (error) {
          console.warn('Failed to load workspace states:', error);
        }
      }
    }
  }, [currentUser]);

  // Save to localStorage when states change
  useEffect(() => {
    if (currentUser?.id) {
      localStorage.setItem(
        `workspace_states_${currentUser.id}`,
        JSON.stringify(workspaceStates)
      );
    }
  }, [workspaceStates, currentUser]);

  // Sidebar state management
  const [sidebarState, setSidebarState] = useState({
    isOpen: false,
    isCollapsed: false
  });

  const toggleSidebar = () => {
    setSidebarState(prev => ({
      ...prev,
      isOpen: !prev.isOpen
    }));
  };

  const toggleSidebarCollapse = () => {
    const newCollapsed = !sidebarState.isCollapsed;
    setSidebarState(prev => ({
      ...prev,
      isCollapsed: newCollapsed
    }));
    
    // Save preference for current mode
    updateCurrentState({ sidebarCollapsed: newCollapsed });
  };

  // Load sidebar state for current mode
  useEffect(() => {
    const currentState = getCurrentWorkspaceState();
    setSidebarState(prev => ({
      ...prev,
      isCollapsed: currentState.sidebarCollapsed || false
    }));
  }, [currentMode]);

  // Theme and layout preferences
  const [layoutPreferences, setLayoutPreferences] = useState({
    density: 'comfortable', // comfortable, compact, spacious
    theme: 'auto', // light, dark, auto
    animations: true,
    notifications: true
  });

  const updateLayoutPreferences = (updates) => {
    setLayoutPreferences(prev => ({
      ...prev,
      ...updates
    }));
    
    // Save to current workspace state
    updateCurrentState({
      preferences: {
        ...getCurrentWorkspaceState().preferences,
        layout: {
          ...getCurrentWorkspaceState().preferences.layout,
          ...updates
        }
      }
    });
  };

  // Filter and search state
  const [filterState, setFilterState] = useState({
    searchQuery: '',
    activeFilters: {},
    sortBy: 'created',
    sortOrder: 'desc',
    viewMode: 'grid' // grid, list, cards
  });

  const updateFilters = (updates) => {
    setFilterState(prev => ({
      ...prev,
      ...updates
    }));
    
    updateCurrentState({
      activeFilters: {
        ...filterState,
        ...updates
      }
    });
  };

  const clearFilters = () => {
    setFilterState({
      searchQuery: '',
      activeFilters: {},
      sortBy: 'created',
      sortOrder: 'desc',
      viewMode: 'grid'
    });
    
    updateCurrentState({ activeFilters: {} });
  };

  // Selection state for bulk operations
  const [selectionState, setSelectionState] = useState({
    selectedItems: [],
    selectAll: false,
    selectionMode: false
  });

  const toggleSelection = (itemId) => {
    setSelectionState(prev => {
      const newSelected = prev.selectedItems.includes(itemId)
        ? prev.selectedItems.filter(id => id !== itemId)
        : [...prev.selectedItems, itemId];
      
      const newState = {
        ...prev,
        selectedItems: newSelected,
        selectAll: false
      };
      
      updateCurrentState({ selectedItems: newSelected });
      return newState;
    });
  };

  const toggleSelectAll = (allItems) => {
    setSelectionState(prev => {
      const newSelectAll = !prev.selectAll;
      const newSelected = newSelectAll ? allItems : [];
      
      const newState = {
        ...prev,
        selectedItems: newSelected,
        selectAll: newSelectAll
      };
      
      updateCurrentState({ selectedItems: newSelected });
      return newState;
    });
  };

  const clearSelection = () => {
    setSelectionState({
      selectedItems: [],
      selectAll: false,
      selectionMode: false
    });
    
    updateCurrentState({ selectedItems: [] });
  };

  const enterSelectionMode = () => {
    setSelectionState(prev => ({
      ...prev,
      selectionMode: true
    }));
  };

  const exitSelectionMode = () => {
    setSelectionState({
      selectedItems: [],
      selectAll: false,
      selectionMode: false
    });
    
    updateCurrentState({ selectedItems: [] });
  };

  const value = {
    // Workspace state management
    workspaceStates,
    getCurrentWorkspaceState,
    updateCurrentState,
    saveWorkspaceState,
    
    // Sidebar state
    sidebarState,
    toggleSidebar,
    toggleSidebarCollapse,
    
    // Layout preferences
    layoutPreferences,
    updateLayoutPreferences,
    
    // Filter and search
    filterState,
    updateFilters,
    clearFilters,
    
    // Selection state
    selectionState,
    toggleSelection,
    toggleSelectAll,
    clearSelection,
    enterSelectionMode,
    exitSelectionMode
  };

  return (
    <WorkspaceStateContext.Provider value={value}>
      {children}
    </WorkspaceStateContext.Provider>
  );
};

export const useWorkspaceState = () => {
  const context = useContext(WorkspaceStateContext);
  if (!context) {
    throw new Error('useWorkspaceState must be used within a WorkspaceStateProvider');
  }
  return context;
};

export default WorkspaceStateContext;